import { useEffect, useRef, useState } from "react";
import { AlertTriangle, CheckCircle2, FileUp, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";
import { hq } from "@/components/anlop/embedCodes";

const GoogleIcon = () => (
  <svg viewBox="0 0 48 48" className="w-4 h-4" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
  </svg>
);

const ROLE = { arrival: "Ankomst", arrival_time: "Ankomsttid", departure: "Avgang", departure_time: "Avgangstid", ship: "Skip", type: "Skipstype", berth: "Kai", status: "Status" };

const when = (iso) => (iso ? new Date(iso).toLocaleString("nb-NO", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "–");

const Mapping = ({ mapping }) => (
  <div className="space-y-2" data-testid="anlop-mapping">
    <div className="text-xs font-medium text-slate-500 uppercase tracking-wide">Kolonner vi fant</div>
    <div className="flex flex-wrap gap-2">
      {mapping.map((m, i) => (
        <span key={i} className={`text-xs rounded-full px-2.5 py-1 border ${m.role ? "bg-sky-50 border-sky-200 text-sky-900" : "bg-slate-50 border-slate-200 text-slate-600"}`} data-testid={`anlop-col-${i}`}>
          {m.header}{m.role && <span className="ml-1 font-semibold">→ {ROLE[m.role]}</span>}
        </span>
      ))}
    </div>
    <p className="text-xs text-slate-500">Blå kolonner vises i hovedlisten. Grå kolonner vises når man åpner en rad.</p>
  </div>
);

export const SourceCard = ({ cid, pid, settings, status, onChange }) => {
  const [url, setUrl] = useState(settings.source_url);
  useEffect(() => { setUrl(settings.source_url); }, [settings.source_url]);
  const [busy, setBusy] = useState("");
  const fileRef = useRef(null);
  const run = async (kind, fn, ok) => {
    setBusy(kind);
    try {
      const { data } = await fn();
      onChange((d) => ({ settings: data.settings || d.settings, status: data.status }));
      toast.success(ok);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };
  const save = () => run("save", () => api.post(`/customers/${cid}/anlop/source${hq(pid)}`, { url }), url ? "Dokumentet er hentet" : "Lenken er fjernet");
  const refresh = () => run("refresh", () => api.post(`/customers/${cid}/anlop/refresh${hq(pid)}`), "Oppdatert fra Google");
  const upload = (f) => {
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    run("upload", () => api.post(`/customers/${cid}/anlop/upload${hq(pid)}`, fd), "Filen er lastet inn");
    fileRef.current.value = "";
  };
  const fromFile = status.source_type === "file" && !settings.source_url;
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 sm:p-5 space-y-5" data-testid="anlop-source-card">
      <div className="space-y-1.5">
        <span className="text-sm font-medium text-slate-700">Google-lenke</span>
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="flex flex-1 gap-2 min-w-0">
            <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://docs.google.com/spreadsheets/d/…" className="font-num text-sm" data-testid="anlop-url-input" />
            <Button variant="outline" size="icon" className="shrink-0" disabled={!/^https?:\/\//.test(url.trim())} title="Åpne dokumentet i Google" aria-label="Åpne dokumentet i Google"
              onClick={() => window.open(url.trim(), "_blank", "noopener")} data-testid="anlop-url-open"><GoogleIcon /></Button>
          </div>
          <Button onClick={save} disabled={!!busy || url === settings.source_url} data-testid="anlop-url-save">
            {busy === "save" && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}Hent og lagre
          </Button>
          {settings.source_url && (
            <Button variant="outline" onClick={refresh} disabled={!!busy} data-testid="anlop-refresh">
              <RefreshCw className={`w-4 h-4 mr-1.5 ${busy === "refresh" ? "animate-spin" : ""}`} />Oppdater nå
            </Button>
          )}
        </div>
        <p className="text-xs text-slate-500">Vi leser første ark eller første tabell. Kolonnenavn og rekkefølge hentes fra dokumentet.</p>
      </div>
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <span className="text-slate-500">Har du ikke Google?</span>
        <input ref={fileRef} type="file" accept=".docx,.csv" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="anlop-file-input" />
        <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={!!busy} data-testid="anlop-file-button">
          {busy === "upload" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FileUp className="w-4 h-4 mr-1.5" />}Last opp Word- eller CSV-fil
        </Button>
        <span className="text-xs text-slate-400">Filer oppdateres ikke automatisk.</span>
      </div>
      {status.error && (
        <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-800" data-testid="anlop-source-error">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /><span>{status.error}{status.rows > 0 && " Listen viser siste vellykkede henting."}</span>
        </div>
      )}
      {status.rows > 0 && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50/60 px-4 py-3 space-y-1 text-sm" data-testid="anlop-source-status">
          <div className="flex items-center gap-2 font-medium text-emerald-900"><CheckCircle2 className="w-4 h-4" />{status.rows} anløp funnet{fromFile && ` i «${status.file_name}»`}</div>
          <div className="text-xs text-emerald-900/80">
            {fromFile ? `Lastet opp ${when(status.fetched_at)}` : `Sist sjekket ${when(status.checked_at)} · sist endret ${when(status.changed_at)} · sjekkes hvert 5. minutt`}
          </div>
        </div>
      )}
      {status.mapping.length > 0 && <Mapping mapping={status.mapping} />}
    </div>
  );
};
